import { useEffect, useMemo, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import BurnerAnalysisService, { InstituteRow, InstituteSearchCriteria } from './BurnerAnalysisService';
import FiltersApplied from './FiltersApplied';
import Grid from './Grid';
import RunAnalysisModal from './RunAnalysisModal';
import SearchCriteria from './SearchCriteria';

interface LoaderData {
    data: {
        searchCriteria: InstituteSearchCriteria;
        instituteList: InstituteRow[];
        summary: { instituteCount: number; totalAlumni: number; burnersDone: number };
    };
}

const useDebounced = <T,>(value: T, delay = 300): T => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
};

const BurnerAnalysisInstitutes = () => {
    const { data } = useLoaderData() as LoaderData;

    const [criteria, setCriteria] = useState<InstituteSearchCriteria>(data.searchCriteria);
    const [rows, setRows] = useState<InstituteRow[]>(data.instituteList);
    const [summary, setSummary] = useState(data.summary);
    const [showFilters, setShowFilters] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [modalInstitute, setModalInstitute] = useState<InstituteRow | null>(null);

    const debouncedCriteria = useDebounced(criteria, 300);
    const criteriaKey = useMemo(() => JSON.stringify(debouncedCriteria), [debouncedCriteria]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        BurnerAnalysisService.searchInstitutes(debouncedCriteria)
            .then((res) => {
                if (cancelled) return;
                setRows(res.data.institutes ?? []);
                setSummary(res.data.summary ?? { instituteCount: 0, totalAlumni: 0, burnersDone: 0 });
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [criteriaKey]);

    const clearAll = () => setCriteria(BurnerAnalysisService.emptySearchCriteria());

    const handleRunCompleted = () => {
        BurnerAnalysisService.searchInstitutes(debouncedCriteria).then((res) => {
            setRows(res.data.institutes ?? []);
            setSummary(res.data.summary ?? { instituteCount: 0, totalAlumni: 0, burnersDone: 0 });
        });
    };

    return (
        <div className="main flex-1">
            <div className="mb-4">
                <div className="white-block white-block-notopborderradius p-0 h-100">
                    <div className="p-4 p-md-5">
                        <div className="alumni_statistics">
                            <div className="d-flex justify-content-between align-items-start pb-3">
                                <div>
                                    <h2 className="font-26 font-600 mb-0">Burner Analysis — Institutes</h2>
                                    <p className="font-13 text-muted mb-0">
                                        Monitor and trigger AI-powered alumni profile analysis across institutes
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="btn-grpfiter"
                                    onClick={() => setShowFilters((v) => !v)}>
                                    <i className="icon-filter font-13 me-2"></i>
                                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                                </button>
                            </div>

                            <div className="ActiveListings_present">
                                {showFilters && (
                                    <SearchCriteria criteria={criteria} onChange={setCriteria} />
                                )}

                                <FiltersApplied
                                    criteria={criteria}
                                    onChange={setCriteria}
                                    onClearAll={clearAll}
                                />

                                {isLoading && (
                                    <div className="text-center text-muted font-13 py-2">Loading institutes…</div>
                                )}

                                <Grid
                                    rows={rows}
                                    summary={summary}
                                    onRunInstitute={(institute) => setModalInstitute(institute)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {modalInstitute && (
                <RunAnalysisModal
                    target={{
                        type: 'institute',
                        id: modalInstitute.instituteId,
                        name: modalInstitute.instituteName,
                        totalAlumni: modalInstitute.totalAlumni,
                    }}
                    onClose={() => setModalInstitute(null)}
                    onCompleted={handleRunCompleted}
                />
            )}
        </div>
    );
};

export default BurnerAnalysisInstitutes;
